import { z } from 'zod';
/**
 * Taschengeld-Contracts (07 – Datenmodell, ADR-012/013). Beträge IMMER in Cent
 * (Integer). Kontostand = Summe der Transaktionen (Cache). Negativer Stand
 * erlaubt (ADR-015). Nur Eltern buchen; Kinder sehen read-only + eigene Sparziele.
 */
export const ALLOWANCE_INTERVALS = ['weekly', 'monthly'];
export const ALLOWANCE_TXN_TYPES = ['credit_auto', 'credit_manual', 'expense', 'bonus', 'correction', 'interest_credit', 'interest_debit'];
/** Vom Elternteil manuell buchbare Typen (credit_auto entsteht nur im Scheduler). */
export const MANUAL_TXN_TYPES = ['credit_manual', 'expense', 'bonus', 'correction'];
// ── DTOs ──────────────────────────────────────────────────────────
export const allowanceAccountDtoSchema = z.object({
    id: z.string().uuid(),
    memberId: z.string().uuid(),
    currency: z.string(),
    balanceCents: z.number().int(),
    reservedCents: z.number().int(), // Summe der Sparziel-Reservierungen
    overdraftLimitCents: z.number().int(), // Dispo-Rahmen (≥ 0), pro Kind (ADR-064)
    createdAt: z.string(),
});
/** Dispo-Rahmen eines Kontos setzen (nur Eltern). Max 10.000 € Rahmen. */
export const setOverdraftSchema = z.object({
    overdraftLimitCents: z.number().int().min(0).max(1_000_000),
});
// ── Bank-Konditionen (Zinsen, familienweit; ADR-064) ──────────────
// Zinssätze als Basispunkte PRO MONAT (100 = 1 %). Default Haben 3 %, Soll 2 %.
export const bankConfigDtoSchema = z.object({
    creditRateBpsMonthly: z.number().int().min(0).max(5000),
    debitRateBpsMonthly: z.number().int().min(0).max(5000),
    interestActive: z.boolean(),
});
export const updateBankConfigSchema = bankConfigDtoSchema;
export const allowanceTxnDtoSchema = z.object({
    id: z.string().uuid(),
    accountId: z.string().uuid(),
    amountCents: z.number().int(),
    type: z.enum(ALLOWANCE_TXN_TYPES),
    note: z.string().nullable(),
    relatedListItemId: z.string().uuid().nullable(),
    goalId: z.string().uuid().nullable(),
    createdBy: z.string().uuid().nullable(),
    createdAt: z.string(),
});
export const allowanceRuleDtoSchema = z.object({
    id: z.string().uuid(),
    accountId: z.string().uuid(),
    amountCents: z.number().int(),
    interval: z.enum(ALLOWANCE_INTERVALS),
    dayOfWeek: z.number().int().nullable(),
    dayOfMonth: z.number().int().nullable(),
    nextRunAt: z.string(),
    active: z.boolean(),
});
export const savingsGoalDtoSchema = z.object({
    id: z.string().uuid(),
    accountId: z.string().uuid(),
    title: z.string(),
    targetCents: z.number().int(),
    reservedCents: z.number().int(),
    achievedAt: z.string().nullable(),
});
// ── Inputs ────────────────────────────────────────────────────────
export const createAccountSchema = z.object({ memberId: z.string().uuid() });
export const createTxnSchema = z.object({
    amountCents: z.number().int().refine((n) => n !== 0, 'Betrag darf nicht 0 sein'),
    type: z.enum(MANUAL_TXN_TYPES),
    note: z.string().max(200).optional(),
});
export const upsertRuleSchema = z
    .object({
    amountCents: z.number().int().positive(),
    interval: z.enum(ALLOWANCE_INTERVALS),
    dayOfWeek: z.number().int().min(0).max(6).nullable().optional(),
    dayOfMonth: z.number().int().min(1).max(31).nullable().optional(),
    active: z.boolean().optional(),
})
    .refine((d) => (d.interval === 'weekly' ? d.dayOfWeek != null : d.dayOfMonth != null), {
    message: 'weekly braucht dayOfWeek, monthly braucht dayOfMonth',
});
export const createGoalSchema = z.object({
    title: z.string().min(1).max(80),
    targetCents: z.number().int().positive(),
});
export const updateGoalSchema = z.object({
    title: z.string().min(1).max(80).optional(),
    targetCents: z.number().int().positive().optional(),
    reserveDeltaCents: z.number().int().optional(), // +zurücklegen / −zurückholen
});
// ── Noten-Prämien ─────────────────────────────────────────────────
// Feste Prämien für Schulnoten. kind: report = Zeugnisnote, exam = Arbeit/HÜ.
// Beträge in Cent, pro Familie editierbar (Eltern). Schnelleingabe bucht bonus.
export const GRADE_REWARD_KINDS = ['report', 'exam'];
/** Standard-Prämien (Cent): Note 1/2/3 × Zeugnis/Arbeit·HÜ. */
export const DEFAULT_GRADE_REWARDS = [
    { grade: 1, kind: 'report', amountCents: 300 },
    { grade: 1, kind: 'exam', amountCents: 200 },
    { grade: 2, kind: 'report', amountCents: 200 },
    { grade: 2, kind: 'exam', amountCents: 100 },
    { grade: 3, kind: 'report', amountCents: 100 },
    { grade: 3, kind: 'exam', amountCents: 50 },
];
export const gradeRewardDtoSchema = z.object({
    grade: z.number().int().min(1).max(6),
    kind: z.enum(GRADE_REWARD_KINDS),
    amountCents: z.number().int().min(0),
});
export const updateGradeRewardsSchema = z.object({
    rewards: z.array(gradeRewardDtoSchema).max(24),
});
export const gradeBonusSchema = z.object({
    grade: z.number().int().min(1).max(6),
    kind: z.enum(GRADE_REWARD_KINDS),
});
//# sourceMappingURL=allowance.js.map